import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UserPlus,
  Search,
  Check,
  X as XIcon,
  Trash2,
  Flame,
  Trophy,
  Crown,
  Sparkles,
  Loader2,
  Gift,
  AlertTriangle,
  ShieldCheck,
  MessageCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Friends() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(true);
  const [giftTarget, setGiftTarget] = useState(null); // user object
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(null); // { type, id }
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/friends`);
      setData(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const search = async (e) => {
    e?.preventDefault();
    setErr("");
    setInfo("");
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await axios.get(
        `${API}/users/search?q=${encodeURIComponent(query.trim())}`
      );
      setResults(data.results || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const sendRequest = async (handle) => {
    setBusy({ type: "request", id: handle });
    setErr("");
    setInfo("");
    try {
      await axios.post(`${API}/friends/request`, { email_or_handle: handle });
      setInfo(`Friend request sent to ${handle}.`);
      setResults([]);
      setQuery("");
      refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send request.");
    } finally {
      setBusy(null);
    }
  };

  const respond = async (request_id, accept) => {
    setBusy({ type: "respond", id: request_id });
    try {
      await axios.post(`${API}/friends/respond`, { request_id, accept });
      refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not respond.");
    } finally {
      setBusy(null);
    }
  };

  const removeFriend = async (user_id) => {
    if (!window.confirm("Remove this friend?")) return;
    setBusy({ type: "remove", id: user_id });
    try {
      await axios.delete(`${API}/friends/${user_id}`);
      refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not remove.");
    } finally {
      setBusy(null);
    }
  };

  const saveStreak = async (friend) => {
    if (!window.confirm(`Save ${friend.full_name || friend.name}'s ${friend.activity_streak}-day streak for 50 Phoenix Tokens?`)) return;
    setBusy({ type: "save", id: friend.user_id });
    try {
      const { data } = await axios.post(`${API}/friends/streak-save`, {
        to_user_id: friend.user_id,
      });
      toast.success(
        `🚨 You saved ${data.to_name}'s ${data.saved_streak_days}-day streak! −50 Phoenix Tokens`,
        { duration: 5000 }
      );
      refreshUser?.();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save streak.");
    } finally {
      setBusy(null);
    }
  };

  const messageFriend = async (friend) => {
    setBusy({ type: "message", id: friend.user_id });
    try {
      const { data } = await axios.post(`${API}/messages/conversations`, {
        type: "dm",
        member_ids: [friend.user_id],
      });
      window.location.href = `/messages?conv=${data.conversation_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open chat.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="friends-section"
      className="bg-[#0F0F0F] border border-zinc-800 p-6 mt-6"
    >
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Inner Circle
          </div>
          <h2 className="font-display text-3xl text-white">
            Friends{" "}
            <span className="text-[#D4AF37]">({data.friends?.length || 0})</span>
          </h2>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={search} className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
          />
          <input
            data-testid="friends-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find by email, name, or @handle"
            className="input-omni pl-9"
          />
        </div>
        <button
          data-testid="friends-search-btn"
          type="submit"
          disabled={searching}
          className="btn-omni btn-outline-gold text-xs disabled:opacity-50"
        >
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Search
        </button>
      </form>

      {err && (
        <div
          data-testid="friends-error"
          className="text-sm text-[#EF4444] border border-[#DC2626] bg-[#DC2626]/10 px-3 py-2 mb-3"
        >
          {err}
        </div>
      )}
      {info && (
        <div
          data-testid="friends-info"
          className="text-sm text-[#D4AF37] border border-[#D4AF37] bg-[#D4AF37]/10 px-3 py-2 mb-3"
        >
          {info}
        </div>
      )}

      {/* Search results */}
      {results.length > 0 && (
        <div data-testid="friends-search-results" className="space-y-2 mb-5">
          {results.map((u) => (
            <UserRow
              key={u.user_id}
              u={u}
              right={
                <button
                  data-testid={`friends-add-${u.user_id}`}
                  onClick={() => sendRequest(u.social_instagram || u.full_name || u.name)}
                  disabled={busy?.type === "request"}
                  className="btn-omni btn-red text-xs disabled:opacity-50"
                >
                  <UserPlus size={12} /> Add
                </button>
              }
            />
          ))}
        </div>
      )}

      {/* Quick add by exact email/handle */}
      {results.length === 0 && query.trim().length >= 2 && !searching && (
        <div className="mb-5">
          <button
            data-testid="friends-quick-add"
            onClick={() => sendRequest(query.trim())}
            className="btn-omni btn-outline-gold text-xs w-full"
          >
            <UserPlus size={12} /> Send request to "{query.trim()}"
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-neutral-500 py-6 text-sm">
          <Loader2 size={20} className="animate-spin inline-block mr-2" />
          Loading…
        </div>
      ) : (
        <>
          {/* Incoming requests */}
          {data.incoming?.length > 0 && (
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#DC2626] font-bold mb-2">
                Pending — Awaiting Your Response ({data.incoming.length})
              </div>
              <div className="space-y-2">
                {data.incoming.map((r) => (
                  <UserRow
                    key={r.request_id}
                    u={r.user}
                    right={
                      <div className="flex gap-1.5">
                        <button
                          data-testid={`friends-accept-${r.request_id}`}
                          onClick={() => respond(r.request_id, true)}
                          disabled={busy?.id === r.request_id}
                          className="w-9 h-9 bg-[#D4AF37] hover:bg-[#f5d96b] text-black flex items-center justify-center"
                          title="Accept"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          data-testid={`friends-decline-${r.request_id}`}
                          onClick={() => respond(r.request_id, false)}
                          disabled={busy?.id === r.request_id}
                          className="w-9 h-9 bg-[#0A0A0A] border border-zinc-700 hover:border-[#DC2626] text-white flex items-center justify-center"
                          title="Decline"
                        >
                          <XIcon size={14} />
                        </button>
                      </div>
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Outgoing requests */}
          {data.outgoing?.length > 0 && (
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
                Sent — Waiting ({data.outgoing.length})
              </div>
              <div className="space-y-2">
                {data.outgoing.map((r) => (
                  <UserRow
                    key={r.request_id}
                    u={r.user}
                    right={
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                        Pending
                      </span>
                    }
                    muted
                  />
                ))}
              </div>
            </div>
          )}

          {/* Friends list */}
          {data.friends?.length > 0 ? (
            <div className="space-y-2">
              {data.friends.map((u) => (
                <UserRow
                  key={u.user_id}
                  u={u}
                  right={
                    <div className="flex items-center gap-1.5">
                      {u.streak_at_risk && !u.streak_already_saved_today && (
                        <button
                          data-testid={`friends-save-streak-${u.user_id}`}
                          onClick={() => saveStreak(u)}
                          disabled={busy?.id === u.user_id}
                          className="px-2 h-9 bg-[#DC2626] hover:bg-red-700 text-white border border-[#DC2626] flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 animate-pulse"
                          title={`Save their ${u.activity_streak}-day streak for 50 Phoenix Tokens`}
                        >
                          <AlertTriangle size={12} />
                          Save 50 Phoenix Tokens
                        </button>
                      )}
                      {u.streak_already_saved_today && (
                        <span
                          className="px-2 h-9 bg-[#0A0A0A] border border-emerald-700/60 text-emerald-400 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest"
                          title="Streak already saved today"
                        >
                          <ShieldCheck size={12} />
                          Saved
                        </span>
                      )}
                      <button
                        data-testid={`friends-message-${u.user_id}`}
                        onClick={() => messageFriend(u)}
                        disabled={busy?.id === u.user_id}
                        className="w-9 h-9 bg-[#0A0A0A] border border-zinc-700 hover:border-[#D4AF37] text-neutral-400 hover:text-[#D4AF37] flex items-center justify-center"
                        title="Send a message"
                      >
                        <MessageCircle size={14} />
                      </button>
                      <button
                        data-testid={`friends-gift-${u.user_id}`}
                        onClick={() => setGiftTarget(u)}
                        className="w-9 h-9 bg-[#0A0A0A] border border-zinc-700 hover:border-[#D4AF37] text-neutral-400 hover:text-[#D4AF37] flex items-center justify-center"
                        title="Send a gift"
                      >
                        <Gift size={14} />
                      </button>
                      <button
                        data-testid={`friends-remove-${u.user_id}`}
                        onClick={() => removeFriend(u.user_id)}
                        disabled={busy?.id === u.user_id}
                        className="w-9 h-9 bg-[#0A0A0A] border border-zinc-700 hover:border-[#DC2626] text-neutral-500 hover:text-[#DC2626] flex items-center justify-center"
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  }
                />
              ))}
            </div>
          ) : (
            !data.incoming?.length &&
            !data.outgoing?.length && (
              <div className="text-center text-neutral-500 py-6 text-sm">
                No friends yet. Search by email, name, or @handle to start your
                circle.
              </div>
            )
          )}
        </>
      )}
      {giftTarget && (
        <GiftModal
          target={giftTarget}
          me={user}
          onClose={() => setGiftTarget(null)}
          onSent={() => {
            refreshUser?.();
            setGiftTarget(null);
          }}
        />
      )}
    </div>
  );
}

function GiftModal({ target, me, onClose, onSent }) {
  const [currency, setCurrency] = useState("pt");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const balance = currency === "pt" ? me?.tokens || 0 : me?.omni_dollars || 0;
  const maxDay = currency === "pt" ? 200 : 50;

  const submit = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) {
      toast.error("Enter an amount.");
      return;
    }
    if (amt > balance) {
      toast.error(`Not enough ${currency.toUpperCase()}. Balance: ${balance}`);
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/friends/gift`, {
        to_user_id: target.user_id,
        currency,
        amount: amt,
        message,
      });
      toast.success(
        `🎁 Sent ${amt} ${currency.toUpperCase()} to ${data.to_name}!`,
        { duration: 4500 }
      );
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gift failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="gift-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Send a Gift
            </div>
            <h3 className="font-display text-2xl text-white leading-tight">
              {target.full_name || target.name}
            </h3>
          </div>
          <button
            data-testid="gift-close"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
          >
            <XIcon size={22} />
          </button>
        </div>

        {/* Currency toggle */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            data-testid="gift-currency-pt"
            onClick={() => setCurrency("pt")}
            className={`py-3 border-2 font-bold uppercase tracking-widest text-xs transition-colors ${
              currency === "pt"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
            }`}
          >
            🐦‍🔥 Phoenix Tokens
          </button>
          <button
            data-testid="gift-currency-od"
            onClick={() => setCurrency("od")}
            className={`py-3 border-2 font-bold uppercase tracking-widest text-xs transition-colors ${
              currency === "od"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
            }`}
          >
            ☯️ Omni Dollars
          </button>
        </div>

        <div className="text-xs text-neutral-500 mb-4 flex items-center justify-between">
          <span>Your balance: <strong className="text-[#D4AF37]">{balance} {currency.toUpperCase()}</strong></span>
          <span>Daily cap: {maxDay}</span>
        </div>

        <input
          data-testid="gift-amount"
          type="text"
          inputMode="numeric"
          className="input-omni w-full mb-3"
          placeholder={`Amount in ${currency.toUpperCase()}`}
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
        />
        <textarea
          data-testid="gift-message"
          className="input-omni w-full mb-4 min-h-[80px]"
          placeholder="Add a message (optional)"
          maxLength={200}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <button
          data-testid="gift-send"
          onClick={submit}
          disabled={sending || !amount}
          className="btn-omni btn-red w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
          Send Gift
        </button>
      </div>
    </div>
  );
}

function UserRow({ u, right, muted = false }) {
  return (
    <div
      data-testid={`friend-row-${u.user_id}`}
      className={`flex items-center gap-3 p-3 bg-[#0A0A0A] border border-zinc-900 ${
        muted ? "opacity-70" : ""
      }`}
    >
      {u.picture ? (
        <img
          src={u.picture}
          alt={u.name}
          className="w-12 h-12 object-contain bg-black border border-[#D4AF37]"
        />
      ) : (
        <div className="w-12 h-12 bg-[#4C1D95] border border-[#D4AF37] flex items-center justify-center font-display text-xl text-[#D4AF37]">
          {(u.name || "?").charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-display text-base text-white truncate">
            {u.full_name || u.name}
          </span>
          {u.role === "owner" && (
            <span
              className="bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1"
              title="Owner"
            >
              <Crown size={9} strokeWidth={2.5} /> Owner
            </span>
          )}
          {u.is_premium && u.role !== "owner" && (
            <span className="bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1">
              <Sparkles size={9} /> Premium
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-neutral-500 mt-0.5">
          <span className="inline-flex items-center gap-1">
            <Flame size={11} className="text-[#DC2626]" /> {u.signin_streak}d
          </span>
          <span className="inline-flex items-center gap-1">
            <Trophy size={11} className="text-[#D4AF37]" /> {u.badges_count}
          </span>
          {u.social_instagram && (
            <span className="truncate">@{u.social_instagram}</span>
          )}
        </div>
      </div>
      <div className="flex-shrink-0">{right}</div>
    </div>
  );
}
